import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../store/authStore';
import {
  useHostCapabilitiesQuery,
  useMyBrandRulesQuery,
} from '../services/brandRulesService/__generated__/queries.generated';
import { getBrand } from '../constants/brands';
import { CarCardPriceMode, carCardPriceModeOf } from '../utils/carCardPrice';
import {
  ALL_CAPABILITIES_ON,
  capabilityView,
  CapabilityView,
  HostCapabilities,
  HostCapabilityKey,
  managedByText,
  normalizeCapabilities,
} from '../utils/hostCapabilities';

export interface UseHostCapabilities {
  capabilities: HostCapabilities;
  /** True until the first answer; everything reads as on meanwhile. */
  loading: boolean;
  /** The switch itself: false when the brand took it from its hosts. */
  can: (capability: HostCapabilityKey) => boolean;
  /** The signed-in user is this site's brand admin or the platform owner. */
  isAdmin: boolean;
  /** Show, show marked "Admin only", or hide (spec 014b US-1). */
  view: (capability: HostCapabilityKey) => CapabilityView;
  /** Shown to this user: the switch is on, or the user is an admin. */
  visible: (capability: HostCapabilityKey) => boolean;
  /** Switched off for hosts but shown to this admin. */
  adminOnly: (capability: HostCapabilityKey) => boolean;
  /** The "managed by" line for a switched-off capability. */
  noteFor: (capability: HostCapabilityKey) => string;
}

/**
 * Spec 014a / 014b: the site's host switches (`hostCapabilities`) for hiding
 * host entries. Signed out, or while loading, everything reads as on, so
 * nothing flickers away. `user.isAdmin` is the site brand's view (the server
 * computes it per request brand), so an admin of another brand is a host here.
 */
export function useHostCapabilities(): UseHostCapabilities {
  const { isAuthenticated, user } = useSnapshot(authStore);
  const { data, loading } = useHostCapabilitiesQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const isAdmin = Boolean(isAuthenticated && user?.isAdmin);

  return useMemo(() => {
    const capabilities = data?.hostCapabilities
      ? normalizeCapabilities(data.hostCapabilities)
      : ALL_CAPABILITIES_ON;
    const brandName = getBrand().name;
    const view = (capability: HostCapabilityKey) =>
      capabilityView(capabilities, capability, isAdmin);
    return {
      capabilities,
      loading: isAuthenticated && loading && !data,
      can: capability => capabilities[capability],
      isAdmin,
      view,
      visible: capability => view(capability) !== 'hidden',
      adminOnly: capability => view(capability) === 'adminOnly',
      noteFor: capability => managedByText(capability, brandName),
    };
  }, [data, loading, isAuthenticated, isAdmin]);
}

export interface BrandRulesView {
  /** The brand uses one daily rate: no weekend, holiday or per-day prices. */
  uniformDailyRate: boolean;
  hourlyRentalOffered: boolean;
  monthlyRentalOffered: boolean;
  /** The youngest driver this site accepts (the brand's age, else its floor, else 18). */
  effectiveMinimumAge: number;
  /** The brand's floor for a car's age limit when hosts set it; null = 18. */
  minimumAge: number | null;
  ageSetByBrand: boolean;
  /** Spec 014c: false when this brand does not offer airport delivery. */
  deliveryOffered: boolean;
  /** Spec 022 D8: guest car cards show the daily price (default) or the week price. */
  carCardPrice: CarCardPriceMode;
  loading: boolean;
}

/**
 * Spec 014b: this site's public rules (`myBrandRules`), for guests and hosts.
 * Before the answer, today's behavior: hourly, monthly and delivery offered, age 18, daily card
 * prices.
 */
export function useBrandRules(): BrandRulesView {
  const { data, loading } = useMyBrandRulesQuery({ fetchPolicy: 'cache-first' });
  return useMemo(() => {
    const rules = data?.myBrandRules;
    return {
      uniformDailyRate: rules?.uniformDailyRate ?? false,
      hourlyRentalOffered: rules?.hourlyRentalOffered ?? true,
      monthlyRentalOffered: rules?.monthlyRentalOffered ?? true,
      effectiveMinimumAge: rules?.effectiveMinimumAge ?? 18,
      minimumAge: rules?.minimumAge ?? null,
      ageSetByBrand: rules?.ageSetByBrand ?? false,
      deliveryOffered: rules?.deliveryOffered ?? true,
      carCardPrice: carCardPriceModeOf(rules?.carCardPrice),
      loading: loading && !data,
    };
  }, [data, loading]);
}
